/** El libro en Google Sheets, leído directamente desde el dispositivo con el token del usuario (drive.file). */
import { DATA_RANGES, OPTIONAL_RANGES, type Values, type WorkbookRanges } from '@sut/workbook';
import type { WorkbookSource } from '../app/ports.js';

type Token = () => Promise<string>;

async function google<T>(url: string, token: Token): Promise<T> {
  const r = await fetch(url, { headers: { authorization: `Bearer ${await token()}` } });
  if (!r.ok) {
    const j = await r.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(r.status === 404 ? 'La app ya no tiene acceso al libro (¿lo has borrado o movido?)' : `Google: ${j.error?.message ?? r.status}`);
  }
  return r.json() as Promise<T>;
}

export function sheetsSource(fileId: string, token: Token): WorkbookSource {
  const base = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(fileId)}`;
  const batch = (ranges: readonly string[]) => google<{ valueRanges: Array<{ values?: Values }> }>(
    `${base}/values:batchGet?${new URLSearchParams([...ranges.map(r => ['ranges', r]), ['valueRenderOption', 'UNFORMATTED_VALUE'], ['dateTimeRenderOption', 'SERIAL_NUMBER']])}`, token);
  const meta = () => google<{ name: string; modifiedTime: string }>(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?fields=name,modifiedTime`, token);
  return {
    id: fileId,
    meta,
    async readData() {
      const keys = Object.keys(DATA_RANGES) as Array<keyof typeof DATA_RANGES>;
      // Pestañas opcionales aparte: si una no existe, Google rechaza todo el lote; así solo se marca como ausente (null)
      const optional = Object.keys(OPTIONAL_RANGES) as Array<keyof typeof OPTIONAL_RANGES>;
      const [data, extra] = await Promise.all([
        batch(keys.map(k => DATA_RANGES[k])),
        Promise.all(optional.map(k => batch([OPTIONAL_RANGES[k]]).then(r => r.valueRanges[0]?.values ?? [], () => null))),
      ]);
      return Object.fromEntries([
        ...keys.map((k, i) => [k, data.valueRanges[i]?.values ?? []]),
        ...optional.map((k, i) => [k, extra[i] ?? null]),
      ]) as WorkbookRanges;
    },
  };
}
